import { useTranslation } from 'react-i18next';
import type { OpticalJobDetail, OpticalJobLensSnapshot } from '@optiflow/types';
import { Card, CardHeader } from '@/components/ui/Card';

/** A frame's sizes as opticians write them, 52□18 140; nothing when none was recorded. */
function sizes(frame: NonNullable<OpticalJobDetail['frame']>): string | null {
  const { lensWidthMm, bridgeWidthMm, templeLengthMm } = frame;
  if (lensWidthMm === null && bridgeWidthMm === null && templeLengthMm === null) return null;
  return `${lensWidthMm ?? '–'}□${bridgeWidthMm ?? '–'} ${templeLengthMm ?? '–'}`;
}

/**
 * The frame, the lens for each eye and the fitting heights, as they were sold.
 *
 * Read from the job's snapshots, never from the catalogue as it stands today: a renamed product next year does not rewrite this pair. A LEGACY job that never recorded a snapshot says so rather than showing anything made up.
 */
export function JobItemsCard({ job }: { job: OpticalJobDetail }) {
  const { t } = useTranslation();
  const frame = job.frame;
  const frameName = frame ? [frame.productName, frame.variantName].filter(Boolean).join(' — ') : '';
  const frameSizes = frame ? sizes(frame) : null;

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.job.itemsTitle')}
        subtitle={t('opticalOrders.job.itemsHint')}
      />
      <dl className="divide-line divide-y text-sm">
        <div className="grid gap-1 px-5 py-3 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-4">
          <dt className="text-muted">{t('opticalOrders.steps.frame')}</dt>
          <dd className="min-w-0 space-y-0.5">
            {frame && (frameName || frame.sku) ? (
              <>
                <p className="text-ink font-medium break-words" dir="auto">
                  {frameName || frame.sku}
                </p>
                <p className="text-muted text-xs">
                  {frame.sku ? (
                    <span className="font-mono" dir="ltr">
                      {frame.sku}
                    </span>
                  ) : null}
                  {frame.brandName ? ` · ${frame.brandName}` : ''}
                  {frame.colorName ? ` · ${frame.colorName}` : ''}
                  {frameSizes ? (
                    <>
                      {' · '}
                      <span className="font-mono" dir="ltr">
                        {frameSizes}
                      </span>
                    </>
                  ) : null}
                </p>
              </>
            ) : (
              <p className="text-muted">{t('opticalOrders.tracking.frameNotRecorded')}</p>
            )}
          </dd>
        </div>
        <LensRow eye="OD" lens={job.lenses.right} fitting={job.rightFittingHeightMm} />
        <LensRow eye="OS" lens={job.lenses.left} fitting={job.leftFittingHeightMm} />
      </dl>
    </Card>
  );
}

function LensRow({
  eye,
  lens,
  fitting,
}: {
  eye: 'OD' | 'OS';
  lens: OpticalJobLensSnapshot | null;
  fitting: string | null;
}) {
  const { t } = useTranslation();
  const coatings = lens
    ? [
        lens.hasAntiReflective ? t('opticalOrders.job.coatings.antiReflective') : null,
        lens.hasScratchResistant ? t('opticalOrders.job.coatings.scratchResistant') : null,
        lens.hasBlueLightFilter ? t('opticalOrders.job.coatings.blueLight') : null,
        lens.isPhotochromic ? t('opticalOrders.job.coatings.photochromic') : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="grid gap-1 px-5 py-3 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-muted">
        <span className="font-mono" dir="ltr">
          {eye}
        </span>{' '}
        · {t(eye === 'OD' ? 'opticalOrders.job.rightEye' : 'opticalOrders.job.leftEye')}
      </dt>
      <dd className="min-w-0 space-y-0.5">
        {lens ? (
          <>
            <p className="text-ink font-medium break-words" dir="auto">
              {[lens.productName, lens.variantName].filter(Boolean).join(' — ')}
            </p>
            <p className="text-muted text-xs">
              <span className="font-mono" dir="ltr">
                {lens.sku}
              </span>
              {' · '}
              {t(`catalog.lensType.${lens.lensType}`)} ·{' '}
              {t(`catalog.lensMaterial.${lens.material}`)}
              {lens.refractiveIndex ? (
                <>
                  {' · '}
                  <span dir="ltr">{lens.refractiveIndex}</span>
                </>
              ) : null}
              {' · '}
              {t(`opticalOrders.supply.${lens.supplyMode}`)}
            </p>
            {coatings.length > 0 ? (
              <p className="text-muted text-xs">{coatings.join(' · ')}</p>
            ) : null}
          </>
        ) : (
          <p className="text-muted">{t('opticalOrders.tracking.lensNotRecorded')}</p>
        )}
        <p className="text-xs">
          <span className="text-muted">{t('opticalOrders.job.fittingHeight')}: </span>
          {fitting ? (
            <span className="text-ink font-mono" dir="ltr">
              {fitting} mm
            </span>
          ) : (
            <span className="text-muted">{t('opticalOrders.job.notRecorded')}</span>
          )}
        </p>
      </dd>
    </div>
  );
}
